import React, { Component } from 'react'
import ReactResizeDetector from 'react-resize-detector'
import {
  XYPlot,
  XAxis,
  YAxis,
  LineSeries,
  VerticalBarSeries,
  HorizontalGridLines,
  DiscreteColorLegend,
  Crosshair,
  RadialChart
} from 'react-vis'
import numeral from 'numeral'
import 'numeral/locales/en-gb'
import { isEqual, result, find } from 'libraries/lodash'
import { pushUrl } from 'front-end-core/core/history'
import url from 'front-end-core/core/url'
import { MW_LIST_DEALS } from 'constants/controllers'
import Icon from 'components/shared/Icon'
import PanelTitle from 'pages/mwanalytics/PanelTitle'
import {
  FROM_OPERATOR,
  TO_OPERATOR
} from '../mwlistdeals/filters/DateRangeFilter'

numeral.locale('en-gb')

const MAX_TICKS = 15

class ChartBase extends Component {
  get config () {
    return result(
      this.props.documentConfiguration,
      [this.props.dealType, 'parameters', this.props.groupBy],
      null
    )
  }

  getItemLabel = config => ({ label, color }) => {
    if (this.props.labelFormat) {
      return {
        title: this.props.labelFormat(label),
        color
      }
    }
    const restrictingValues = result(config, 'restrictingValues', [])
    const whereMatchesLabel = find(restrictingValues, ({ value }) => value === label)
    return {
      title: result(whereMatchesLabel, 'displayName', label) || <em>Unlabeled</em>,
      color
    }
  }

  format = value => numeral(value).format(this.props.tickFormat || '0')

  get info () {
    return this.props.info
      ? (
        <div key='info' className='f7 tl flex justify-start items-start ml2'>
          <Icon className='mr2 pt2' name='icon-information-outline' />
          {this.props.info}
        </div>
        )
      : null
  }
}

class GraphChart extends ChartBase {
  state = {
    crosshair: [],
    hilite: null
  }

  crosshairValue = name => (value, { event, innerX, innerY, index }) => {
    this.setState({ crosshair: this.getValuesFor(index) })
  }

  shouldComponentUpdate (props, state) {
    return !isEqual(this.state, state) || !isEqual(this.props, props)
  }

  getValuesFor = index => {
    return this.props.stats.data.map(({ plot }) => plot[index])
  }

  get yAxisTickCount () {
    return 2
  }

  onClick = e => {
    const { crosshair } = this.state
    const { dealType, aol } = this.props
    if (crosshair.length) {
      const [{ range: [from, to] }] = crosshair
      const n = this.dateParameterName
      const parameters = [
        { n, o: FROM_OPERATOR, v: [from] },
        { n, o: TO_OPERATOR, v: [to] }
      ]
      const route = url(MW_LIST_DEALS, { aol, dealType }, { parameters: JSON.stringify(parameters) })
      pushUrl(route)
    }
  }

  get dateParameterName () {
    const params = result(this.props.documentConfiguration, [this.props.dealType, 'parameters'], [])
    const typeNames = find(params, ({ filterGroup: { type: group }, type }) =>
      group === 'Basic' && type === 'DateParameterType'
    )

    return result(typeNames, 'name', null)
  }

  get tickSpacing () {
    return Math.floor(this.props.stats.data[0].plot.length / MAX_TICKS) + 1
  }

  get plotLineSeries () {
    return this.props.stats.data.map(
      ({ name, label, plot, options, color }, index) => (
        <LineSeries
          key={name}
          data={plot}
          curve='curveLinear'
          {...options}
          color={color}
          {...this.getOpacity(index)}
          onNearestX={!index ? this.crosshairValue(name) : undefined}
        />
      )
    )
  }

  get plotBarSeries () {
    return [
      ...this.props.stats.data.map(
        ({ name, label, plot, options, color }, index) => (
          <VerticalBarSeries
            key={name}
            data={plot}
            {...options}
            color={color}
            {...this.getOpacity(index)}
            onNearestX={!index ? this.crosshairValue(name) : undefined}
          />
        )
      )
    ].reverse()
  }

  getOpacity (index) {
    if (this.state.hilite === null) {
      return {}
    }
    return {
      opacity: this.state.hilite === index ? 1 : 0.2
    }
  }

  render () {
    const { stats: { data }, displayName, groupBy } = this.props
    const { crosshair } = this.state
    const getItemLabel = this.getItemLabel(this.config)
    const { format, yAxisTickCount, tickSpacing, plotBarSeries, plotLineSeries } = this

    return (
      <div>
        <PanelTitle {...{ displayName }} />
        {data.length
          ? (
              [
                <ReactResizeDetector key='plot' handleWidth>
                  <ResizeXYPlot
                    onMouseLeave={() => this.setState({ crosshair: [] })}
                    handleClick={this.onClick}
                    {...{
                      crosshair,
                      getItemLabel,
                      groupBy,
                      format,
                      yAxisTickCount,
                      tickSpacing,
                      plotBarSeries,
                      plotLineSeries,
                      data
                    }}
                  />
                </ReactResizeDetector>,
                <DiscreteColorLegend
                  key='legend'
                  items={data.map(getItemLabel)}
                  onItemMouseEnter={(item, index) => this.setState({ hilite: index })}
                  onItemMouseLeave={() => this.setState({ hilite: null })}
                />,
                this.info
              ]
            )
          : (
            <div>No data available</div>
            )}
      </div>
    )
  }
}

const ResizeXYPlot = ({
  width = 0,
  groupBy,
  crosshair,
  onMouseLeave,
  handleClick,
  format,
  yAxisTickCount,
  tickSpacing,
  plotBarSeries,
  plotLineSeries,
  getItemLabel,
  data
}) => {
  const stackBy = groupBy ? 'y' : undefined

  return (
    <div className='flex justify-center w-100' style={{ height: 300 }}>
      {!!width && (
        <XYPlot
          xType='ordinal'
          stackBy={stackBy}
          height={300}
          width={width * 0.95}
          onMouseLeave={onMouseLeave}
          onClick={handleClick}
          style={{ cursor: 'pointer' }}
        >
          <HorizontalGridLines />
          <YAxis
            className=''
            tickSizeInner={0}
            tickSizeOuter={0}
            tickPadding={5}
            tickFormat={format}
            tickTotal={yAxisTickCount}
          />
          <XAxis
            tickSizeInner={0}
            tickSizeOuter={4}
            tickPadding={15}
            tickLabelAngle={-45}
            tickFormat={(x, index) => index % tickSpacing === 0 ? x : ''}
          />
          {groupBy ? plotBarSeries : plotLineSeries}
          <Crosshair values={crosshair}>
            <div className='bg-white black pa4 br2 shadow-black-3 max-w14 nowrap'>
              <div className='b mb2'>
                {result(crosshair, [0, 'x'])}
              </div>
              <div className='flex flex-column' data-testid='graph-details'>
                {crosshair
                  .map(({ y }, index) => ({
                    render: (
                      <div
                        key={index}
                        className='items-center flex mb4'
                      >
                        <div className='h0 pb3 w5 dib' style={{ background: data[index].color }}>
                          &nbsp;
                        </div>
                        <div className='w12 truncate pl4'>
                          {getItemLabel(data[index]).title}
                        </div>
                        <div className='tr grow-1'>
                          {format(y)}
                        </div>
                      </div>
                    ),
                    y
                  }))
                  .filter(({ y }) => !!y)
                  .map(({ render }) => render)}
              </div>
            </div>
          </Crosshair>
        </XYPlot>
      )}
    </div>
  )
}

class InfoBox extends ChartBase {
  render () {
    const config = this.config
    const getLabel = this.getItemLabel(config)
    const { displayName, stats } = this.props

    return (
      <div>
        <PanelTitle displayName={displayName} />
        <div className='flex flex-column grow-1'>
          {stats.data.map(({ label, name, plot }, key) => (
            <div className='flex pa4 flex-column' key={key}>
              <span>{getLabel({ label }).title}</span>
              <strong>{this.format(plot[0].y)}</strong>
            </div>
          ))}
        </div>
      </div>
    )
  }
}

class PieChart extends ChartBase {
  get data () {
    return this.props.stats.data.map(
      ({ plot: [{ y: value }], label, color }) => ({
        angle: value,
        label: this.format(value),
        value,
        color,
        style: { fill: color, stroke: '#FFF', strokeWidth: 1 }
      })
    )
  }

  get items () {
    return this.props.stats.data.map(v => {
      const { title, color } = this.getItemLabel(this.config)(v)
      return {
        title: (
          <span>
            {title}: {this.format(v.plot[0].y)}
          </span>
        ),
        color
      }
    })
  }

  state = {
    hover: null
  }

  render () {
    const { data, props: { displayName } } = this

    return (
      <div>
        <PanelTitle {...{ displayName }} />
        {data.length
          ? (
              [
                <RadialChart
                  key='chart'
                  data={data}
                  width={300}
                  height={300}
                  onValueMouseOver={({ label, value }) =>
                    this.setState({ hover: { x: label, y: value } })}
                  onSeriesMouseOut={() => this.setState({ hover: null })}
                />,
                <DiscreteColorLegend key='legend' items={this.items} />,
                this.info
              ]
            )
          : (
            <div>No data available</div>
            )}
      </div>
    )
  }
}

export default props =>
  props.interval !== 'a'
    ? (
      <GraphChart {...props} />
      )
    : props.groupBy
      ? (
        <PieChart {...props} />
        )
      : (
        <InfoBox {...props} />
        )
